import { createServerFn } from "@tanstack/react-start";
import { setResponseHeader, setResponseStatus } from "@tanstack/react-start/server";
import type { CmsPageLoadInput } from "./cms-page.server";

const normalizeSearchParams = (value: unknown): CmsPageLoadInput["searchParams"] => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const searchParams: NonNullable<CmsPageLoadInput["searchParams"]> = {};

  for (const [key, rawValue] of Object.entries(value as Record<string, unknown>)) {
    if (typeof rawValue === "string") {
      searchParams[key] = rawValue;
      continue;
    }

    if (Array.isArray(rawValue)) {
      searchParams[key] = rawValue.filter((item): item is string => typeof item === "string");
    }
  }

  return searchParams;
};

const normalizeCmsPageInput = (value: unknown): CmsPageLoadInput => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { path: "/", searchParams: {} };
  }

  const record = value as Record<string, unknown>;
  const path = typeof record.path === "string" && record.path.trim() ? record.path : "/";

  return {
    path: path.startsWith("/") ? path : `/${path}`,
    searchParams: normalizeSearchParams(record.searchParams),
  };
};

export const loadCmsPageServer = createServerFn({ method: "GET" })
  .validator(normalizeCmsPageInput)
  .handler(async ({ data }) => {
    const { loadCmsPage } = await import("./cms-page.server");
    const result = await loadCmsPage(data);

    if (result.statusCode) {
      setResponseStatus(result.statusCode);
    }

    for (const [name, value] of Object.entries(result.cache.headers)) {
      setResponseHeader(name as never, value);
    }

    return result;
  });
